import { requestJson } from "../api/apiClient";

/**
 * @typedef {Object} DealerVehicleListingIdentityMetadata
 * @property {string} marketListingId - Explicit `vehicle_market_listings.id` (equals dealer response `id`).
 * @property {string|null} mobilityListingId - Linked `vehicle_listing.id` when exactly one valid mapping exists.
 * @property {string|null} canonicalVehicleId - Cross-marketplace grouping id (`canonical_vehicle.id`).
 * @property {string|null} canonicalPublicUrl - Canonical public route using mobility listing id.
 */

const BASE = "/api/partner/automap";
const AUTH_OPTIONS = { skipAuthRedirect: true };

function buildQs(params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value != null && value !== "");
  if (!entries.length) return "";
  return `?${new URLSearchParams(Object.fromEntries(entries)).toString()}`;
}

export function fetchPartnerInventoryHealth(params = {}) {
  return requestJson(`${BASE}/inventory-health${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchPartnerDealers(params = {}) {
  return requestJson(`${BASE}/dealers${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchPartnerDealerAccounts(params = {}) {
  return requestJson(`${BASE}/dealers${buildQs({ ...params, view: "accounts" })}`, AUTH_OPTIONS);
}

export function fetchPartnerDealerAccountProfile(accountId) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(accountId)}/account-profile`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerDealerAccountNotes(accountId) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(accountId)}/notes`, AUTH_OPTIONS);
}

export function addPartnerDealerAccountNote(accountId, payload) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(accountId)}/notes`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchPartnerDealer(dealerId) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(dealerId)}`, AUTH_OPTIONS);
}

export function updatePartnerDealer(dealerId, payload) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(dealerId)}`, {
    ...AUTH_OPTIONS,
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function reorderPartnerDealerPriority(dealerIds) {
  return requestJson(`${BASE}/dealers/priority-order`, {
    ...AUTH_OPTIONS,
    method: "PUT",
    body: JSON.stringify({ dealerIds }),
  });
}

export function fetchPartnerDealerInventory(dealerId, params = {}) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerDealerListing(dealerId, listingId) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerVehicleMakes(params = {}) {
  return requestJson(`${BASE}/vehicle-lookups/makes${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchPartnerVehicleModels(params = {}) {
  return requestJson(`${BASE}/vehicle-lookups/models${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchPartnerVehicleTrims(params = {}) {
  return requestJson(`${BASE}/vehicle-lookups/trims${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchPartnerDealerNormalizationSummary(dealerId) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/normalization-summary`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerDealerGroupedInventory(dealerId, params = {}) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/inventory/grouped${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerDealerInventoryHealth(dealerId, params = {}) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/inventory/health${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerInventoryRolloutConfig(params = {}) {
  return requestJson(`${BASE}/inventory-rollout/config${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchPartnerInventoryRolloutReadiness() {
  return requestJson(`${BASE}/inventory-rollout/readiness`, AUTH_OPTIONS);
}

export function fetchPartnerDealerNormalizationReview(dealerId, params = {}) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/normalization-review${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchPartnerPortfolioNormalizationReview(params = {}) {
  return requestJson(
    `${BASE}/inventory-health/normalization-review${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function normalizePartnerDealerListing(dealerId, listingId, payload) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}/normalize`,
    {
      ...AUTH_OPTIONS,
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function bulkNormalizePartnerDealerListings(dealerId, payload) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/bulk-normalize`,
    {
      ...AUTH_OPTIONS,
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function createPartnerVehicleAliasSuggestion(payload) {
  return requestJson(`${BASE}/vehicle-lookups/alias-suggestions`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createPartnerDealerListing(dealerId, payload) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(dealerId)}/listings`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updatePartnerDealerListing(dealerId, listingId, payload) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}`,
    {
      ...AUTH_OPTIONS,
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function runPartnerDealerListingAction(dealerId, listingId, action, payload = {}) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/listings/${encodeURIComponent(listingId)}/actions`,
    {
      ...AUTH_OPTIONS,
      method: "POST",
      body: JSON.stringify({ action, ...payload }),
    }
  );
}

export function fetchPartnerDealerUsers(dealerId) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(dealerId)}/users`, AUTH_OPTIONS);
}

export function createPartnerDealerUser(dealerId, payload) {
  return requestJson(`${BASE}/dealers/${encodeURIComponent(dealerId)}/users`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updatePartnerDealerUser(dealerId, userId, payload) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/users/${encodeURIComponent(userId)}`,
    {
      ...AUTH_OPTIONS,
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function startPartnerDealerPortalSession(dealerId, payload = {}) {
  return requestJson(
    `${BASE}/dealers/${encodeURIComponent(dealerId)}/dealer-portal-session`,
    {
      ...AUTH_OPTIONS,
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}
